import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { LookupItem } from '../../store/api/saleContractApi'
import {
  type SalePTBFFixation,
  type SalePTBFFixationPayload,
  type SalePTBFFixationRow,
  type SalePTBFFixationRowUpdate,
  useCreateSalePTBFFixationMutation,
  useGetSalePTBFFixationLotToFixQuery,
  useGetSalePTBFFixationQuery,
  useGetSalePTBFFixationRowsQuery,
  useLazyGetSalePTBFFixationContractsQuery,
  useUpdateSalePTBFFixationEditOptionsMutation,
  useUpdateSalePTBFFixationRowMutation,
  useCancelSalePTBFFixationMutation,
} from '../../store/api/salePTBFFixationsApi'
import ConfirmDialog from '../../components/ConfirmDialog'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface SalePTBFFixationFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  contractNo?: string | null
  onClose: () => void
}

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatNum = (value: number | null | undefined): string => {
  if (value == null) return ''
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface SalePTBFFixationFormModel {
  contractNo: string
  dateFix: string
  releaseDate: string
  lotsFixed: string
  levelFixed: string
  isUpload: boolean
  isCusSign: boolean
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): SalePTBFFixationFormModel => ({
  contractNo: '',
  dateFix: today(),
  releaseDate: today(),
  lotsFixed: '' as string,
  levelFixed: '' as string,
  isUpload: false,
  isCusSign: false,
})

const safeDate = (value: unknown): string => {
  if (!value) return ''
  const text = String(value)
  return text.length >= 10 ? text.slice(0, 10) : text
}

const toNum = (value: string): number | null => {
  if (!value) return null
  const n = Number(value)
  return isNaN(n) ? null : n
}

const apiToForm = (row: SalePTBFFixation): SalePTBFFixationFormModel => ({
  contractNo: row.contractNo ?? '',
  dateFix: safeDate(row.dateFix) || today(),
  releaseDate: safeDate(row.releaseDate) || today(),
  lotsFixed: row.lotsFixed != null ? String(row.lotsFixed) : '',
  levelFixed: row.levelFixed != null ? String(row.levelFixed) : '',
  isUpload: Boolean(row.isUpload),
  isCusSign: Boolean(row.isCusSign),
})

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save PTBF Fixation. Please try again.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save PTBF Fixation. Please try again.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save PTBF Fixation. Please try again.'
}

const roSx = {
  '& .MuiInputBase-root': { bgcolor: 'action.hover' },
  '& .MuiInputBase-input': { color: 'text.secondary', cursor: 'default' },
}

const dialogContentSx = {
  pt: 0.75,
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiCheckbox-root': { p: 0.5 },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
  '& .MuiSelect-select': { fontSize: '0.8125rem' },
}

/* ──────────────────────────────────────────────
   Sub-dialog: edit individual PF fixation row
────────────────────────────────────────────── */
interface RowEditDialogProps {
  open: boolean
  row: SalePTBFFixationRow | null
  lotToFix: number | null
  onClose: () => void
  onSaved: () => void
}

function RowEditDialog({ open, row, lotToFix, onClose, onSaved }: RowEditDialogProps) {
  const [dateFix, setDateFix] = useState('')
  const [lotsFixed, setLotsFixed] = useState('')
  const [levelFixed, setLevelFixed] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [updateRow, { isLoading }] = useUpdateSalePTBFFixationRowMutation()

  useEffect(() => {
    if (!open || !row) return
    setDateFix(safeDate(row.dateFix) || today())
    setLotsFixed(row.lotsFixed != null ? String(row.lotsFixed) : '')
    setLevelFixed(row.levelFixed != null ? String(row.levelFixed) : '')
    setError(null)
  }, [open, row])

  const handleSave = async () => {
    if (!dateFix) { setError('Please choose Date Fixed!'); return }
    if (lotsFixed === '') { setError('Please type Lots Fixed!'); return }
    if (levelFixed === '') { setError('Please type Level Fixed!'); return }
    if (lotToFix != null && Number(lotsFixed) > Number(lotToFix)) {
      setError("'Lots Fixed' must not be greater than 'Lot to fix'!")
      return
    }
    setError(null)
    try {
      const body: SalePTBFFixationRowUpdate = {
        dateFix,
        lotsFixed: toNum(lotsFixed),
        levelFixed: toNum(levelFixed),
      }
      await updateRow({ ptbfFixationId: row!.ptbfFixationId!, body }).unwrap()
      onSaved()
      onClose()
    } catch (e) {
      setError(extractApiErrorMessage(e))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          Sale Contract - PTBF Fixation
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={isLoading}>
            {isLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={dialogContentSx}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contract No"
              size="small"
              fullWidth
              value={row?.contractNo ?? ''}
              InputProps={{ readOnly: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lot to fix"
              size="small"
              fullWidth
              value={lotToFix ?? ''}
              InputProps={{ readOnly: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Date fix" type="date" size="small" fullWidth value={dateFix} onChange={(e) => setDateFix(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lots fixed"
              size="small"
              fullWidth
              value={fmtNumStr(lotsFixed)}
              onChange={(e) => setLotsFixed(e.target.value.replace(/,/g, ''))}
              inputProps={{ inputMode: 'decimal' }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Level fixed"
              size="small"
              fullWidth
              value={fmtNumStr(levelFixed)}
              onChange={(e) => setLevelFixed(e.target.value.replace(/,/g, ''))}
              inputProps={{ inputMode: 'decimal' }}
              sx={numSx}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

/* ──────────────────────────────────────────────
   Main form dialog
────────────────────────────────────────────── */
export default function SalePTBFFixationFormDialog({ open, mode, contractNo, onClose }: SalePTBFFixationFormDialogProps) {
  const [form, setForm] = useState<SalePTBFFixationFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [rowEditTarget, setRowEditTarget] = useState<SalePTBFFixationRow | null>(null)
  const [rowEditOpen, setRowEditOpen] = useState(false)

  const [fetchContracts, { data: contractOptions, isFetching: contractsLoading }] =
    useLazyGetSalePTBFFixationContractsQuery()
  const { data: editData, isFetching: editLoading } = useGetSalePTBFFixationQuery(contractNo ?? '', {
    skip: !open || mode !== 'edit' || !contractNo,
  })
  const { data: lotToFixData } = useGetSalePTBFFixationLotToFixQuery(form.contractNo, {
    skip: !open || !form.contractNo,
  })
  const { data: rowsData, refetch: refetchRows } = useGetSalePTBFFixationRowsQuery(contractNo ?? '', {
    skip: !open || mode !== 'edit' || !contractNo,
  })
  const snack = useSnackbar()
  const [rowDeleteTarget, setRowDeleteTarget] = useState<SalePTBFFixationRow | null>(null)
  const [createFixation, { isLoading: creating }] = useCreateSalePTBFFixationMutation()
  const [updateEditOptions, { isLoading: updating }] = useUpdateSalePTBFFixationEditOptionsMutation()
  const [cancelFixation, { isLoading: isDeleting }] = useCancelSalePTBFFixationMutation()

  const contractItems = useMemo(() => contractOptions?.items ?? [], [contractOptions])
  const lotToFix = lotToFixData?.lotToFix ?? lotToFixData?.balanceLotsTbf ?? null
  const subformRows = useMemo(() => rowsData?.items ?? [], [rowsData])
  const rowCount = subformRows.length

  useEffect(() => {
    if (!open) return
    setValidationError(null)
    if (mode === 'new') {
      setForm(emptyForm())
      fetchContracts(undefined, false)
    }
  }, [open, mode, fetchContracts])

  useEffect(() => {
    if (open && mode === 'edit' && editData) {
      setForm(apiToForm(editData))
    }
  }, [open, mode, editData])

  useEffect(() => {
    if (mode === 'new' && form.contractNo && !form.releaseDate) {
      setForm((prev) => ({ ...prev, releaseDate: today() }))
    }
  }, [form.contractNo, form.releaseDate, mode])

  const set = <K extends keyof SalePTBFFixationFormModel>(key: K, value: SalePTBFFixationFormModel[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const setNum = (key: keyof SalePTBFFixationFormModel, raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw.replace(/,/g, '') }))

  const validateLikeAccess = (): string | null => {
    if (!form.contractNo.trim()) return 'S contract is existed!'
    if (!form.dateFix) return 'Please choose Date Fixed!'
    if (form.lotsFixed === '') return 'Please type Lots Fixed!'
    if (form.levelFixed === '') return 'Please type Level Fixed!'
    if (lotToFix != null && Number(form.lotsFixed) > Number(lotToFix)) return "'Lots Fixed' must not be greater than 'Lot to fix'!"
    return null
  }

  const handleSave = async () => {
    const accessError = mode === 'new' ? validateLikeAccess() : null
    if (accessError) {
      setValidationError(accessError)
      return
    }
    setValidationError(null)
    try {
      if (mode === 'new') {
        const body: SalePTBFFixationPayload = {
          contractNo: form.contractNo,
          dateFix: form.dateFix,
          releaseDate: form.releaseDate || today(),
          lotsFixed: toNum(form.lotsFixed),
          levelFixed: toNum(form.levelFixed),
          isUpload: form.isUpload ? 1 : 0,
          isCusSign: form.isCusSign ? 1 : 0,
        }
        await createFixation(body).unwrap()
      } else if (contractNo) {
        const body: SalePTBFFixationPayload = {
          contractNo: form.contractNo,
          releaseDate: form.releaseDate || today(),
          isUpload: form.isUpload ? 1 : 0,
          isCusSign: form.isCusSign ? 1 : 0,
        }
        await updateEditOptions({ contractNo, body }).unwrap()
      }
      onClose()
    } catch (error) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  const handleRowEdit = (row: SalePTBFFixationRow) => {
    setRowEditTarget(row)
    setRowEditOpen(true)
  }

  const handleRowDelete = (row: SalePTBFFixationRow) => {
    setRowDeleteTarget(row)
  }

  const handleConfirmRowDelete = async () => {
    if (!rowDeleteTarget?.ptbfFixationId) return
    setRowDeleteTarget(null)
    try {
      await cancelFixation(rowDeleteTarget.ptbfFixationId).unwrap()
      refetchRows()
      snack.success('Successfully Deleted')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Could not delete PTBF Fixation.'))
    }
  }

  const title = mode === 'new' ? 'Sale PTBF Fixation' : 'Edit - Sale PTBF Fixation'

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={creating || updating || editLoading}>
              {creating || updating || editLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent dividers sx={dialogContentSx}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {validationError}
            </Alert>
          )}

          {mode === 'new' ? (
            /* ── New form ── */
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  size="small"
                  options={contractItems}
                  loading={contractsLoading}
                  getOptionLabel={(o: LookupItem | string) => (typeof o === 'string' ? o : o.label)}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  inputValue={form.contractNo}
                  onOpen={() => fetchContracts(form.contractNo || undefined)}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      set('contractNo', value)
                      fetchContracts(value || undefined)
                    } else if (reason === 'clear') {
                      set('contractNo', '')
                      fetchContracts(undefined)
                    }
                  }}
                  onChange={(_, item) => set('contractNo', item?.value ?? '')}
                  filterOptions={(x) => x}
                  renderInput={(params) => <TextField {...params} label="Contract no" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Release Date" type="date" size="small" fullWidth value={form.releaseDate} onChange={(e) => set('releaseDate', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Lot to Fix" size="small" fullWidth value={lotToFix ?? ''} InputProps={{ readOnly: true }} sx={roSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Date fix"
                  type="date"
                  size="small"
                  fullWidth
                  value={form.dateFix}
                  onChange={(e) => set('dateFix', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Lots fixed"
                  size="small"
                  fullWidth
                  value={fmtNumStr(form.lotsFixed)}
                  onChange={(e) => setNum('lotsFixed', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={numSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Level fixed"
                  size="small"
                  fullWidth
                  value={fmtNumStr(form.levelFixed)}
                  onChange={(e) => setNum('levelFixed', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={numSx}
                />
              </Grid>
            </Grid>
          ) : (
            /* ── Edit form ── */
            <Box>
              {/* Header fields */}
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Contract no" size="small" fullWidth value={form.contractNo} InputProps={{ readOnly: true }} sx={roSx} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={<Checkbox checked={form.isUpload} onChange={(e) => set('isUpload', e.target.checked)} />}
                    label="Is Uploaded?"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={<Checkbox checked={form.isCusSign} onChange={(e) => set('isCusSign', e.target.checked)} />}
                    label="Customer Signed?"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Release Date" type="date" size="small" fullWidth value={form.releaseDate} onChange={(e) => set('releaseDate', e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
              </Grid>

              {/* PF Fixation subform */}
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ overflow: 'auto' }}>
                <Table size="small" sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Contract No</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Date fix</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Lots fixed</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Level fixed</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Functions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subformRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 1, fontSize: '0.8125rem' }}>
                          No records
                        </TableCell>
                      </TableRow>
                    ) : (
                      subformRows.map((row) => (
                        <TableRow
                          key={row.ptbfFixationId}
                          hover
                          sx={{ '& td': { py: 0.375, fontSize: '0.8125rem', whiteSpace: 'nowrap' } }}
                        >
                          <TableCell>{row.contractNo ?? ''}</TableCell>
                          <TableCell>{formatAccessDate(row.dateFix)}</TableCell>
                          <TableCell align="right">{formatNum(row.lotsFixed ?? null)}</TableCell>
                          <TableCell align="right">{formatNum(row.levelFixed ?? null)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 0, px: 0.75, py: 0.15, fontSize: '0.72rem', textTransform: 'none' }}
                                onClick={() => handleRowEdit(row)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ minWidth: 0, px: 0.75, py: 0.15, fontSize: '0.72rem', textTransform: 'none' }}
                                onClick={() => handleRowDelete(row)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
              {/* Footer: row count */}
              <Box sx={{ mt: 0.5, px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Count: {rowCount}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Row edit sub-dialog */}
      <RowEditDialog
        open={rowEditOpen}
        row={rowEditTarget}
        lotToFix={lotToFix}
        onClose={() => setRowEditOpen(false)}
        onSaved={() => refetchRows()}
      />
      <ConfirmDialog
        open={rowDeleteTarget != null}
        title="Delete PTBF Fixation"
        message={`Do you want to delete this PTBF Fixation = '${rowDeleteTarget?.contractNo ?? ''}'?`}
        confirmText="Delete"
        destructive
        loading={isDeleting}
        onConfirm={() => void handleConfirmRowDelete()}
        onCancel={() => setRowDeleteTarget(null)}
      />
    </>
  )
}
